import { useContext, useEffect, useRef, useState } from "react"
import ReactDOMServer from "react-dom/server"
import L, { divIcon } from "leaflet"
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet"
import ReactLeafletDriftMarker from "react-leaflet-drift-marker"
import { polylineAtePonto, polylineAposPonto } from "./rotaServicosGeo"
import { htmlIconeGps } from "./rotaServicosIconeGps"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import { buscarMapaProfissional } from "../../../servicos/googleMapsProfissional"
import RotaServicosMapaGoogle from "./RotaServicosMapaGoogle"

const COR_COLETA = "#00ced1"
const COR_ENTREGA = "#f59e0b"

export function corTipoPonto(tipoPonto) {
	return tipoPonto === "coleta" ? COR_COLETA : COR_ENTREGA
}

function iconParada(n, proximo, tipoPonto = "entrega", contraRotacao = 0) {
	const cor = corTipoPonto(tipoPonto)
	const letra = tipoPonto === "coleta" ? "C" : "E"
	const tam = proximo ? 40 : 32
	return divIcon({
		html: ReactDOMServer.renderToString(
			<div
				className={`rs-pin-tipo${proximo ? " rs-pin-prox" : ""}`}
				style={{
					width: tam,
					height: tam,
					position: "relative",
					transform: contraRotacao ? `rotate(${contraRotacao}deg)` : undefined,
					["--rs-pin"]: cor,
				}}
			>
				<div
					style={{
						width: tam,
						height: tam,
						borderRadius: "50%",
						background: cor,
						color: "#fff",
						fontWeight: 800,
						fontSize: proximo ? 15 : 13,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						border: "2px solid #fff",
						boxShadow: "0 2px 8px rgba(0,0,0,.28)",
						letterSpacing: "0.02em",
					}}
				>
					{letra}
				</div>
				<span
					style={{
						position: "absolute",
						right: -3,
						bottom: -3,
						minWidth: 16,
						height: 16,
						padding: "0 4px",
						borderRadius: 99,
						background: "#111",
						color: "#fff",
						fontSize: 9,
						fontWeight: 800,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						border: "1.5px solid #fff",
						lineHeight: 1,
					}}
				>
					{n}
				</span>
			</div>
		),
		className: "rs-pin",
		iconSize: [tam, tam],
		iconAnchor: [tam / 2, tam / 2],
	})
}

function iconVoce(heading, iconeArquivo) {
	const gps = htmlIconeGps(heading, iconeArquivo)
	if (gps) {
		return divIcon({
			html: gps,
			className: "rs-pin-voce",
			iconSize: [72, 72],
			iconAnchor: [36, 36],
		})
	}
	return divIcon({
		html: ReactDOMServer.renderToString(
			<div style={{ width: 36, height: 36, position: "relative" }}>
				<div className="rs-seta-halo" />
				<div
					className="rs-seta"
					style={{
						position: "absolute",
						left: 9,
						top: 4,
						transform: `rotate(${Number(heading) || 0}deg)`,
					}}
				/>
			</div>
		),
		className: "rs-pin-voce",
		iconSize: [36, 36],
		iconAnchor: [18, 22],
	})
}

function latLngsParaEnquadrar(positions, polyline) {
	const pts = []
	for (const p of positions || []) {
		if (Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])) pts.push(p)
	}
	if (!Array.isArray(polyline) || !polyline.length) return pts
	const passo = polyline.length > 240 ? Math.ceil(polyline.length / 240) : 1
	for (let i = 0; i < polyline.length; i += passo) {
		const p = polyline[i]
		if (Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])) pts.push(p)
	}
	const last = polyline[polyline.length - 1]
	if (Array.isArray(last) && Number.isFinite(last[0]) && Number.isFinite(last[1])) pts.push(last)
	return pts
}

function AjustarVista({ positions, polyline, sheetPx, tick, giroExpandido }) {
	const map = useMap()

	useEffect(() => {
		const t = setTimeout(() => map.invalidateSize(), 80)
		return () => clearTimeout(t)
	}, [map, sheetPx, giroExpandido])

	useEffect(() => {
		if (giroExpandido) return undefined
		const t = setTimeout(() => {
			map.invalidateSize()
			const pts = latLngsParaEnquadrar(positions, polyline)
			if (!pts.length) return
			if (pts.length === 1) {
				map.setView(pts[0], 15)
				return
			}
			map.fitBounds(L.latLngBounds(pts), {
				paddingTopLeft: [92, 168],
				paddingBottomRight: [76, Math.max((sheetPx || 0) + 28, 148)],
				maxZoom: 16,
			})
		}, 80)
		return () => clearTimeout(t)
	}, [map, tick, giroExpandido])
	return null
}

const ZOOM_PROFISSIONAL = 17

function SeguirProfissional({ pos, ativo, focoTick }) {
	const map = useMap()
	const ultimoFoco = useRef(0)
	const pulando = useRef(false)

	useEffect(() => {
		if (!ativo || !pos) return undefined
		const aproximar = focoTick > 0 && focoTick !== ultimoFoco.current
		if (aproximar) ultimoFoco.current = focoTick
		pulando.current = true
		if (aproximar) {
			map.flyTo([pos.la, pos.lo], ZOOM_PROFISSIONAL, { duration: 0.55, easeLinearity: 0.25 })
		} else {
			map.panTo([pos.la, pos.lo], { animate: true, duration: 0.35 })
		}
		const t = setTimeout(() => {
			pulando.current = false
		}, aproximar ? 700 : 400)
		return () => clearTimeout(t)
	}, [map, pos?.la, pos?.lo, ativo, focoTick])

	useEffect(() => {
		map._rsPulandoZoom = pulando
	}, [map])

	return null
}

function DetectarInteracao({ onInteragir }) {
	const map = useMap()
	useEffect(() => {
		if (!onInteragir) return undefined
		const parar = () => {
			if (map._rsPulandoZoom?.current) return
			onInteragir()
		}
		map.on("dragstart", parar)
		return () => {
			map.off("dragstart", parar)
		}
	}, [map, onInteragir])
	return null
}

function RegistrarMapa({ onMapa }) {
	const map = useMap()
	useEffect(() => {
		onMapa?.(map)
	}, [map, onMapa])
	return null
}

function CliqueNoMapa({ onClick }) {
	const map = useMap()
	useEffect(() => {
		if (!onClick) return undefined
		map.on("click", onClick)
		return () => map.off("click", onClick)
	}, [map, onClick])
	return null
}

export default function RotaServicosMapa({
	pontos,
	polyline,
	posProf,
	heading,
	seguir,
	rotacao = 0,
	focoProf,
	onPararSeguir,
	onMapa,
	onSelect,
	onFecharSheet,
	tick,
	sheetPx,
	iconeProf,
}) {
	const { dados } = useContext(ContextoGeral)
	const [modo, setModo] = useState("leaflet")
	const [chave, setChave] = useState("")
	const mapKey = useRef(`rs-${Date.now()}`)

	useEffect(() => {
		let vivo = true
		buscarMapaProfissional(dados?.dominio).then((cfg) => {
			if (!vivo) return
			if (cfg.mapa && cfg.chave) {
				setChave(cfg.chave)
				setModo("google")
			} else setModo("leaflet")
		})
		return () => { vivo = false }
	}, [dados?.dominio])
	const proximo = pontos[0]
	const locaisProximos = [
		...(posProf ? [[posProf.la, posProf.lo]] : []),
		...pontos.slice(0, 3).map((p) => [p.la, p.lo]),
	]
	const lats = pontos.map((p) => p.la)
	const lons = pontos.map((p) => p.lo)
	const spanGrande =
		pontos.length > 1 &&
		(Math.max(...lats) - Math.min(...lats) > 1.2 || Math.max(...lons) - Math.min(...lons) > 1.2)

	const positions = spanGrande
		? locaisProximos
		: [
			...(posProf ? [[posProf.la, posProf.lo]] : []),
			...pontos.map((p) => [p.la, p.lo]),
		]
	const center = posProf
		? [posProf.la, posProf.lo]
		: pontos[0]
			? [pontos[0].la, pontos[0].lo]
			: [-14.24, -51.92]

	const cor =
		(typeof window !== "undefined" &&
			getComputedStyle(document.documentElement).getPropertyValue("--primaria")?.trim()) ||
		"#100940"
	const trechoAtual = proximo && polyline?.length > 1 ? polylineAtePonto(polyline, proximo) : polyline
	const restoRota = proximo && polyline?.length > 1 ? polylineAposPonto(polyline, proximo) : []

	if (modo === "google") {
		return (
			<RotaServicosMapaGoogle
				chave={chave}
				dominio={dados?.dominio}
				onFalhar={() => setModo("leaflet")}
				pontos={pontos}
				polyline={polyline}
				posProf={posProf}
				heading={heading}
				seguir={seguir}
				rotacao={rotacao}
				focoProf={focoProf}
				onPararSeguir={onPararSeguir}
				onMapa={onMapa}
				onSelect={onSelect}
				onFecharSheet={onFecharSheet}
				tick={tick}
				sheetPx={sheetPx}
				iconeProf={iconeProf}
			/>
		)
	}
	return (
		<MapContainer
			key={mapKey.current}
			center={center}
			zoom={14}
			minZoom={4}
			maxZoom={18}
			zoomControl={false}
			attributionControl={false}
			scrollWheelZoom
			doubleClickZoom
			touchZoom
			dragging
			className="rs-mapa-inner"
			style={{ height: "100%", width: "100%" }}
		>
			<TileLayer
				attribution='&copy; OpenStreetMap'
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
			/>
			<AjustarVista
				positions={positions}
				polyline={polyline}
				sheetPx={sheetPx}
				tick={tick}
				giroExpandido={seguir && focoProf > 0}
			/>
			<SeguirProfissional pos={posProf} ativo={seguir} focoTick={focoProf} />
			<RegistrarMapa onMapa={onMapa} />
			<DetectarInteracao onInteragir={onPararSeguir} />
			<CliqueNoMapa onClick={onFecharSheet} />

			{restoRota.length > 1 && (
				<Polyline
					positions={restoRota}
					pathOptions={{ color: cor, weight: 5, opacity: 0.72, lineCap: "round" }}
				/>
			)}
			{trechoAtual?.length > 1 && (
				<Polyline
					positions={trechoAtual}
					pathOptions={{ color: "#111", weight: 6, opacity: 0.95, lineCap: "round" }}
				/>
			)}

			{pontos.map((p) => {
				const ehProximo = proximo && p.key === proximo.key
				return (
					<Marker
						key={p.key}
						position={[p.la, p.lo]}
						icon={iconParada(p.parada, ehProximo, p.tipoPonto, rotacao)}
						eventHandlers={{ click: () => onSelect(p) }}
						zIndexOffset={ehProximo ? 600 : 400}
					/>
				)
			})}

			{posProf && (
				<ReactLeafletDriftMarker
					position={[posProf.la, posProf.lo]}
					icon={iconVoce(heading, iconeProf)}
					duration={280}
					zIndexOffset={800}
				/>
			)}
		</MapContainer>
	)
}
